import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import {
  flexBoxCenter,
  layerA180Deg,
  layerBDark,
} from '../styles/commonStyles';

export const BLANK_LABEL = 'Input P / N';

const PartNumberSelect = ({
  value,
  options = [],
  isEnable = true,
  isLocked = false,
  onChange,
}) => {
  const [{ isOpen, highlightedIdx }, setUiState] = useState({
    isOpen: false,
    highlightedIdx: 0,
  });
  const wrapperRef = useRef(null);

  const uiRef = useRef({ isOpen: false, highlightedIdx: 0 });
  const setUi = (next) => {
    uiRef.current = next;
    setUiState(next);
  };

  const items = ['', ...options];
  const indexOfValue = Math.max(items.indexOf(value ?? ''), 0);

  const close = () => setUi({ ...uiRef.current, isOpen: false });

  const open = () => setUi({ isOpen: true, highlightedIdx: indexOfValue });

  const select = (nextValue) => {
    onChange(nextValue);
    close();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const step = (offset) =>
    setUi({
      isOpen: true,
      highlightedIdx:
        (uiRef.current.highlightedIdx + offset + items.length) % items.length,
    });

  const handleKeyDown = (event) => {
    const { isOpen: listIsOpen, highlightedIdx: highlighted } = uiRef.current;

    switch (event.key) {
      case 'Escape':
        close();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        listIsOpen ? select(items[highlighted]) : open();
        break;
      case 'ArrowDown':
        event.preventDefault();
        listIsOpen ? step(1) : open();
        break;
      case 'ArrowUp':
        event.preventDefault();
        listIsOpen ? step(-1) : open();
        break;
      default:
        break;
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type='button'
        role='combobox'
        aria-label={BLANK_LABEL}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        title={value || undefined}
        isEnable={isEnable}
        isLocked={isLocked}
        disabled={isLocked}
        onClick={() => (uiRef.current.isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
      >
        <TriggerLabel isPlaceholder={!value}>
          {value || BLANK_LABEL}
        </TriggerLabel>
        <Caret aria-hidden='true'>▾</Caret>
      </Trigger>

      {isOpen && (
        <List role='listbox' aria-label={BLANK_LABEL}>
          <ListInner>
            {items.map((item, index) => (
              <Option
                key={item || '__blank__'}
                role='option'
                aria-selected={item === (value ?? '')}
                isHighlighted={index === highlightedIdx}
                isPlaceholder={!item}
                onMouseEnter={() =>
                  setUi({ isOpen: true, highlightedIdx: index })
                }
                onClick={() => select(item)}
              >
                {item || BLANK_LABEL}
              </Option>
            ))}
          </ListInner>
        </List>
      )}
    </Wrapper>
  );
};

export default PartNumberSelect;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const Trigger = styled.button`
  width: 100%;
  height: 100%;
  ${flexBoxCenter}
  justify-content: space-between;
  gap: 2px;
  padding: 0 3px;

  background-color: transparent;
  border: none;
  opacity: ${(p) => (p.isLocked ? 0.5 : 1)};
  cursor: ${(p) => (p.isLocked ? 'default' : 'pointer')};
  color: ${(p) => (p.isEnable ? '#fff' : '#808080')};
`;

const TriggerLabel = styled.span`
  flex: 1;
  font-size: 9px;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  color: ${(p) => p.isPlaceholder && '#808080'};
`;

const Caret = styled.span`
  font-size: 7px;
  line-height: 1;
`;

const List = styled.ul`
  width: 206px;
  border-radius: 20px;
  padding: 3px;

  ${layerA180Deg};
  ${flexBoxCenter}

  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 1000;
`;

const ListInner = styled.div`
  width: 100%;
  max-height: 132px;
  overflow-y: auto;
  border-radius: 15px;
  ${layerBDark};

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Option = styled.li`
  width: 100%;
  min-height: 24px;
  padding: 4px 6px;

  font-size: 10px;
  letter-spacing: 0.6px;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;

  ${flexBoxCenter}

  ${(p) =>
    p.isPlaceholder &&
    css`
      color: #808080;
    `}

  background-color: ${(p) => p.isHighlighted && 'hsla(50deg, 100%, 80%, 0.25)'};

  &:hover {
    background-color: hsla(50deg, 100%, 80%, 0.25);
  }
`;
